import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { valueForOnlyNumericInputTextFields } from "utils/helpers";

import {
  BODY_TYPE_OPTIONS,
  DRIVER_CABIN_OPTIONS,
  FrontClearanceSelectOption,
} from "../data";
import {
  selectFrontClearanceForm,
  selectFrontClearanceFormDropDowns,
  selectFrontClearanceSaving,
  selectIsAddModalOpen,
} from "../redux/selector";
import { frontClearanceActions } from "../redux/slice";
import {
  FrontClearanceForm,
  FrontClearanceFormDropDownKey,
} from "../types";

export type FieldConfig = {
  key: keyof FrontClearanceForm;
  label: string;
  type: "select" | "text" | "textarea";
  inputType?: "string" | "number";
  options?: FrontClearanceSelectOption[];
  fullWidth?: boolean;
  dropDownKey?: FrontClearanceFormDropDownKey;
};

export const FRONT_CLEARANCE_NUMBER_FIELDS = new Set<keyof FrontClearanceForm>([
  "year",
  "optimalDistance",
  "minimalDistance",
]);

export const sanitizeFrontClearanceFormValue = (
  name: keyof FrontClearanceForm,
  value: string,
) => {
  if (FRONT_CLEARANCE_NUMBER_FIELDS.has(name)) {
    return valueForOnlyNumericInputTextFields(value);
  }
  return value;
};

const REQUIRED_FIELDS: Array<keyof FrontClearanceForm> = [
  "manufacturer",
  "masterModel",
  "year",
  "driverCabin",
  "bodyType",
  "optimalDistance",
  "minimalDistance",
  "note",
];

const P = "FRONT_CLEARANCE.MODAL";

export const useAddFrontClearanceModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectFrontClearanceForm);
  const { manufacturers, superModels } = useSelector(
    selectFrontClearanceFormDropDowns,
  );
  const saving = useSelector(selectFrontClearanceSaving);

  useEffect(() => {
    if (!open) return;
    dispatch(frontClearanceActions.getFormDropDownList("manufacturers"));
    dispatch(frontClearanceActions.getFormDropDownList("superModels"));
  }, [open, dispatch]);

  const fields: FieldConfig[] = [
    {
      key: "manufacturer",
      label: "MANUFACTURER_NAME",
      type: "select",
      inputType: "number",
      dropDownKey: "manufacturers",
      options: manufacturers.items.map(({ id, name }) => ({
        value: String(id),
        label: name,
      })),
    },
    {
      key: "masterModel",
      label: "MASTER_MODEL",
      type: "select",
      inputType: "number",
      dropDownKey: "superModels",
      options: superModels.items.map(({ id, name }) => ({
        value: String(id),
        label: name,
      })),
    },
    { key: "year", label: "YEAR", type: "text", inputType: "number" },
    {
      key: "driverCabin",
      label: "DRIVER_CABIN",
      type: "select",
      inputType: "string",
      options: DRIVER_CABIN_OPTIONS,
    },
    {
      key: "bodyType",
      label: "BODY_TYPE",
      type: "select",
      inputType: "number",
      options: BODY_TYPE_OPTIONS,
      fullWidth: true,
    },
    {
      key: "optimalDistance",
      label: "OPTIMAL_DISTANCE",
      type: "text",
      inputType: "number",
    },
    {
      key: "minimalDistance",
      label: "MINIMAL_DISTANCE",
      type: "text",
      inputType: "number",
    },
    {
      key: "note",
      label: "NOTE",
      type: "textarea",
      inputType: "string",
      fullWidth: true,
    },
  ];

  const onClose = () => {
    if (saving) return;
    dispatch(frontClearanceActions.closeAddModal());
  };

  const setField = (name: keyof FrontClearanceForm, value: string) =>
    dispatch(
      frontClearanceActions.setFormField({
        name,
        value: sanitizeFrontClearanceFormValue(name, value),
      }),
    );

  const placeholder = (label: string, type: "select" | "text" | "textarea") =>
    t(`${P}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`);

  const fieldLabel = (label: string) => t(`${P}.FIELDS.${label}`);

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key].trim()));

  const optionLabel = (option: FrontClearanceSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const onSubmit = () => dispatch(frontClearanceActions.addFrontClearance());

  const onDropDownScroll = (dropDownKey?: FrontClearanceFormDropDownKey) => {
    if (!dropDownKey) return;
    dispatch(frontClearanceActions.getFormDropDownNextPage(dropDownKey));
  };

  const hasMoreDropDownItems = (dropDownKey?: FrontClearanceFormDropDownKey) => {
    if (!dropDownKey) return false;
    const dropDown =
      dropDownKey === "manufacturers" ? manufacturers : superModels;
    return (
      !dropDown.loading &&
      dropDown.meta.currentPage < dropDown.meta.totalPages
    );
  };

  return {
    t,
    open,
    form,
    saving,
    fields,
    isValid,
    addTitle: t(`${P}.ADD_TITLE`),
    onClose,
    setField,
    placeholder,
    fieldLabel,
    optionLabel,
    onSubmit,
    onDropDownScroll,
    hasMoreDropDownItems,
  };
};
